import { useCallback } from "react"
import { api } from "@/api"
import { getCSRF } from "QuorumGrassroots/campaign-forms/helpers"
import {
    RegistrationActionConfig,
    RegistrationType,
    RegistrationTypeValue,
} from "QuorumGrassroots/pages/campaign/voter-information/types"

const REGISTRATION_TEXT: Record<RegistrationTypeValue, string> = {
    [RegistrationType.ONLINE]: "Clicked on online voter registration",
    [RegistrationType.PAPER]: "Clicked on paper voter registration",
}

/**
 * Hook to create supporter actions when a user clicks on voter registration links.
 * The action is created asynchronously (fire-and-forget) to not block user navigation.
 *
 * @param registrationActionConfig - Configuration object containing campaignId, supporterId, organizationId, actionCenterResourceUri
 * @returns A function to create registration actions
 */
export const useCreateRegistrationAction = (registrationActionConfig: RegistrationActionConfig) => {
    const createRegistrationAction = useCallback(
        async (registrationType: RegistrationTypeValue): Promise<void> => {
            const { campaignId, supporterId, organizationId, actionCenterResourceUri } = registrationActionConfig

            if (!supporterId || !organizationId || !actionCenterResourceUri || !campaignId) {
                console.error("[useCreateRegistrationAction] Missing required data for creating registration action", {
                    supporterId,
                    organizationId,
                    actionCenterResourceUri,
                    campaignId,
                })
                return
            }

            try {
                const payload = {
                    objects: [
                        {
                            text: REGISTRATION_TEXT[registrationType],
                            campaign: DjangIO.app.grassroots.campaign.models.Campaign.resourceUriFromId(campaignId),
                            supporter_action_type:
                                DjangIO.app.grassroots.enums.SupporterActionType.submit_gotv_widget_v2.value,
                            interaction_type:
                                registrationType === RegistrationType.ONLINE
                                    ? DjangIO.app.userdata.types.GrassrootsInteractionType.registered_online.value
                                    : DjangIO.app.userdata.types.GrassrootsInteractionType.paper_registration.value,
                            supporter: DjangIO.app.grassroots.models.Supporter.resourceUriFromId(supporterId),
                            organization: DjangIO.app.userdata.models.Organization.resourceUriFromId(organizationId),
                            action_center: actionCenterResourceUri,
                        },
                    ],
                }

                await api.patch(
                    {
                        model: DjangIO.app.grassroots.models.GrassrootsSupporterAction,
                        params: "decode_enums=false&count=false",
                    },
                    payload,
                    {
                        headers: {
                            "X-Csrftoken": getCSRF(),
                        },
                    },
                )
            } catch (error) {
                console.error("[useCreateRegistrationAction] Failed to create registration action", error)
            }
        },
        [registrationActionConfig],
    )

    return { createRegistrationAction }
}
